<template>
    <div>
        <!-- Two separate portal modules render this component:
             - "Termine" (events module, delegate=false) → personal self-booking form only
             - "Einsatzplanung" (delegatePlan module, delegate=true) → the assign-for-others matrix
             The delegate matrix loads its own data via onGetDelegatePlan (no plan-data preload). -->
        <DelegatePlan v-if="delegate" :lang="lang" />
        <Vueform v-else-if="events" class="vue-form" ref="form" name="events" :schema="schema" :displayErrors="false" :endpoint="onSubmitEvents">
        </Vueform>
    </div>
</template>
<script>
import Events from "./customElements/EventsElement.vue";
import DelegatePlan from "./customElements/DelegatePlan.vue";
export default {
    components: {Events, DelegatePlan},
    props: ['applicationId', 'lang', 'delegate'],
    data() {
        return {
            events: {},
            schema: {},
            calendarLink: '',
            loading: false,
            successMessage: '',
            showSuccess: false,
            bookableEvents: [],
            checkDirtyFormListener: undefined,
        };
    },
    mounted() {
        // In delegate mode this component only hosts the DelegatePlan matrix, which fetches
        // its own data — nothing to load here, and there is no Vueform/dirty-check to wire up.
        if (this.delegate) return;
        this.getEvents();
        this.$vueform.i18n.locale = this.lang;
        this.checkDirtyFormListener = (event) => {
            event.isDirty = this.$refs.form.isDirty;
            window.removeEventListener('checkDirty_events', this.checkDirtyFormListener, false);
        }
        window.addEventListener('checkDirty_events', this.checkDirtyFormListener, {once: true});
    },
    beforeUnmount() {
        window.removeEventListener('checkDirty_events', this.checkDirtyFormListener, false);
    },
    methods: {
        getEvents() {
            this.doOctoberRequest('onGetEvents', {applicationId: this.applicationId}).then((result) => {
//                console.log(result);
                if (!result) {
                    UIkit.modal.alert();
                }

                this.bookableEvents = result.bookableEvents;
                this.schema = Object.keys(result.events).length > 0 || Object.keys(result.bookableEvents).length === 0 ? {
                    nonBookableEvents: {
                        type: "events",
                        submit: false,
                        settings: {
                            readonly: true,
                            events: result.events,
                            selected: []
                        }
                    }} : {}
                if (Object.keys(result.events).length > 0) {
                    this.schema.text = {
                        type: "static",
                        content: '<p>'+this.$refs['form'].translations.festiware.calendar.text+' <a href="'+result.calendarLink+'">'+this.$refs['form'].translations.festiware.calendar.link+'</a></p>'
                    };
                }
                const bookableTypeAmount = Object.keys(result.bookableEvents).length;

                if (bookableTypeAmount > 0) {
                    if (bookableTypeAmount > 1) {
                        Object.keys(result.bookableEvents).forEach(key => {
                            this.schema['type_'+key+'_section'] = {
                                type: 'static',
                                content: '<h3>'+result.bookableEvents[key]['typeName']+'</h3>'
                            }
                            this.schema['type_'+key] = {
                                type: 'events',
                                settings: result.bookableEvents[key]
                            }
                            this.$refs.form.data['type_'+key] = {events: [], jobs: []};
                        })
                    } else {
                        this.schema['type_'+Object.keys(result.bookableEvents)[0]] = {
                            type:"events",
                            settings: Object.values(result.bookableEvents)[0]
                        }
                    }
                    this.schema.submit = {
                        type: "button",
                        submits: true,
                        buttonLabel: this.lang === 'de' ? "Speichern" : "Save"
                    }
                }
//                this.events = result.events;
                this.calendarLink = result.calendarLink;
                this.$nextTick(() => {
                    this.$refs.form.isDirty = false;
                })
            })
        },
        onSubmitEvents(FormData, form$) {
            const submitData = {};
            Object.keys(form$.requestData).forEach(key => {
                if (key.startsWith('type_')) {
                    submitData.type_id = key.substring(5);
                    submitData.events = form$.requestData[key];
                }
            })
            const minimum = this.bookableEvents[submitData.type_id]?.minimumSelection;
            if (minimum > 0 && (submitData.events?.events?.length ?? 0) < minimum) {
                UIkit.notification(this.$refs.form.translations.festiware.events.minimum_not_met.replace(':min', minimum));
                return;
            }
            this.doOctoberRequest('onSubmitEvents', submitData).then((result) => {
                if (result.success) {
                    // Success shows a transient toast (no button) like the main form,
                    // then the modal closes (or hands off to the portal redirect handler).
                    UIkit.notification({message: '<span uk-icon="check"></span> ' + result.success, status: 'success', pos: 'top-center', timeout: 6000});
                    if (result.redirect_to) {
                        const root = (this.$root && this.$root.$el) || document.querySelector('#events-app') || document;
                        root.dispatchEvent(new CustomEvent('formSubmitSuccess', {
                            bubbles: true,
                            detail: {redirectTo: result.redirect_to},
                        }));
                    } else {
                        UIkit.modal('#module-modal').hide();
                    }
                } else {
                    if (result.errors.events) {
                      UIkit.modal.alert(result.errors.events[0], {i18n: {ok: 'ok'}})
                    }
                    UIkit.modal.alert(result.errors[0], {i18n: {ok: 'ok'}})
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        doOctoberRequest(requestName, data, messages = undefined, silent = false, withFiles = false) {
            return new Promise((resolve, reject) => {
                oc.ajax(requestName, {
//                    data: data,
                    data: {data: JSON.stringify(data)},
//                    contentType: 'application/json',
                    files: withFiles,
                    progressBar: true,
                    flash: true,
                    error: (jqXHR) => {
                        if (messages && jqXHR.responseJSON) {
                            let message = jqXHR.responseJSON.error;
                            if (
                                messages.error &&
                                messages.errors[jqXHR.responseJSON.errorID]
                            ) {
                                message = this.translate(
                                    messages.errors[jqXHR.responseJSON.errorID]
                                );
                            } else if (messages.error) {
                                message = this.translate(messages.error);
                            }
                        }
                        reject(jqXHR.responseJSON ? jqXHR.responseJSON : jqXHR);
                    },
                    success: (data) => {
                        resolve(data);
                    },
                    complete: () => {
                    },
                });
            }).finally(() => {
                this.loading = false;
            }).catch(e => {
                console.log(e);
                this.hasErrors = true;
                this.errorMessage = e;
            });
        },
    }
}
</script>
